import { RiGlobalLine } from '@remixicon/react';
import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
    type ComponentPropsWithRef,
    type HTMLAttributes,
    type InputHTMLAttributes,
    type ReactNode,
    type RefObject,
} from 'react';
import {
    default as ReactPhoneInput,
    type Country,
    type DefaultInputComponentProps,
    type FeatureProps,
    type Value,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Button from '~/components/new/Button';
import type { IconNames } from '~/components/ui/Icon';
import Icon, { RemixIconNames } from '~/components/ui/Icon';
import {
    applyNumericValidity,
    isPartialNumericInput,
    moneyToNumber,
    numericInputConstraints,
    numericInputMode,
    numericInputPattern,
    parseNumericInput,
    resolveNumericInputRules,
    sanitizeMoneyInput,
} from '~/components/ui/forms/numericInput';
import { NUMERIC_PATTERNS } from '~/components/ui/forms/numericPatterns';
import { classNames } from '~/utils/styles';

export type InputAlign = 'left' | 'right' | 'center' | 'justify' | 'start' | 'end' | 'match-parent';

export type InputProps = ComponentPropsWithRef<'input'> &
    HTMLAttributes<HTMLSpanElement> & {
        className?: string;
        align?: InputAlign;
        icon?: IconNames;
        unmask?: boolean;
        suffix?: string;
        prefix?: string;
        suffixAlignRight?: boolean;
        error?: boolean;
        suffixAtEnd?: boolean;
        onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
        value?: string;
    };

export default function Input(props: InputProps) {
    const {
        className = '',
        type = 'text',
        align = 'left',
        icon,
        unmask = false,
        suffix,
        prefix,
        error = false,
        onChange = () => {},
        value,
        ...HTMLInputProps
    } = props;

    const elementClassName = classNames({
        'form-control': true,
        error,
        icon: Boolean(icon) || Boolean(error),
        'fs-unmask': unmask,
        'form-input-prefixed': Boolean(prefix || suffix),
    });

    if (prefix || suffix) {
        return (
            <PrefixedInput
                {...props}
                className={elementClassName}
            />
        );
    }

    const numeric = type === 'number' ? numericInputConstraints(HTMLInputProps.min) : undefined;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (numeric) {
            if (!isPartialNumericInput(e.target.value, numeric)) return;
            applyNumericValidity(e.currentTarget, HTMLInputProps.min, HTMLInputProps.max);
        }
        onChange(e);
    };

    return (
        <div
            className={classNames({
                'form-input': true,
                [elementClassName]: true,
                [className]: Boolean(props.className),
            })}>
            {error && !icon && (
                <Icon
                    icon={RemixIconNames.RiErrorWarningLine}
                    className='form-input-icon'
                />
            )}
            {icon && (
                <Icon
                    icon={icon}
                    className='form-input-icon'
                />
            )}
            {!prefix && !suffix && (
                <input
                    type={numeric ? 'text' : type}
                    inputMode={numeric ? numericInputMode(numeric) : undefined}
                    pattern={numeric ? numericInputPattern(numeric) : undefined}
                    className={elementClassName}
                    value={value}
                    style={{ textAlign: align }}
                    {...HTMLInputProps}
                    id={HTMLInputProps.id || HTMLInputProps.name} // ensure id is set for label association
                    onChange={handleChange}
                />
            )}
            {props.readOnly && (
                <Icon
                    icon={RemixIconNames.RiLockFill}
                    colorVar='core-tertiary'
                    className='form-input-icon'
                />
            )}
        </div>
    );
}

type PrefixedInputShellProps = {
    prefix?: string;
    suffix?: string;
    icon?: IconNames;
    className?: string;
    value?: string;
    inputRef: RefObject<HTMLInputElement>;
    children: ReactNode;
};

function PrefixedInputShell({
    prefix,
    suffix,
    icon,
    className = '',
    value,
    inputRef,
    children,
}: PrefixedInputShellProps) {
    const mirrorRef = useRef<HTMLElement>(null!);

    const focusInput = () => {
        inputRef.current?.focus();
    };

    useEffect(() => {
        if (!mirrorRef.current || !inputRef.current) return;
        mirrorRef.current.textContent = inputRef.current.value || ' ';
        if (inputRef.current.value) {
            inputRef.current.style.width = mirrorRef.current.offsetWidth + 'px';
        } else {
            inputRef.current.style.width = '';
        }
    }, [value, inputRef]);

    return (
        <div
            className={classNames({
                'form-input': true,
                [className]: Boolean(className),
            })}
            onClick={focusInput}>
            {icon && (
                <Icon
                    icon={icon}
                    className='form-input-icon'
                />
            )}
            {prefix && value !== undefined && value !== '' && (
                <span
                    className='form-input-prefix'
                    onClick={focusInput}>
                    {prefix}
                </span>
            )}
            {children}
            <span
                ref={mirrorRef}
                className='form-input-prefixed-mirror'></span>
            {suffix && value !== undefined && value !== '' && (
                <span
                    className='form-input-suffix'
                    onClick={focusInput}>
                    {suffix}
                </span>
            )}
        </div>
    );
}

function PrefixedInput({
    className = '',
    align = 'left',
    icon,
    suffix,
    prefix,
    suffixAtEnd = false,
    ...props
}: Omit<InputProps, 'error' | 'unmask' | 'type'>) {
    const inputRef = useRef<HTMLInputElement>(null!);

    return (
        <PrefixedInputShell
            prefix={prefix}
            suffix={suffix}
            icon={icon}
            className={className}
            value={typeof props.value === 'string' ? props.value : undefined}
            inputRef={inputRef}>
            <input
                className={classNames({
                    'form-input-prefixed-input': true,
                    'form-input-prefixed-input-wide': suffixAtEnd,
                })}
                ref={inputRef}
                {...props}
                style={{ textAlign: align }}
            />
        </PrefixedInputShell>
    );
}

export type NumberInputProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'prefix' | 'onChange' | 'step' | 'pattern'
> & {
    error?: boolean;
    onChange?: (value: number) => void;
    align?: InputAlign;
    prefix?: string;
    suffix?: string;
    pattern?: string;
    partialPattern?: string;
};

function NumberInput({
    value,
    error = false,
    onChange,
    align = 'left',
    className = '',
    min,
    max,
    prefix,
    suffix,
    pattern = NUMERIC_PATTERNS.DECIMAL,
    partialPattern,
    ...props
}: NumberInputProps) {
    const rules = useMemo(
        () => resolveNumericInputRules({ pattern, partialPattern, min }),
        [pattern, partialPattern, min],
    );
    const inputRef = useRef<HTMLInputElement>(null!);

    const toLocal = (v: NumberInputProps['value']) =>
        v === undefined || v === null || isNaN(Number(v)) ? '' : String(v);

    const [local, setLocal] = useState<string>(toLocal(value));

    useEffect(() => {
        const incoming = value === undefined || value === null ? NaN : Number(value);
        const current = parseNumericInput(local);
        if (!(isNaN(incoming) && isNaN(current)) && incoming !== current) {
            setLocal(toLocal(value));
        }
    }, [value]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        if (!rules.isPartialAllowed(next)) return;
        applyNumericValidity(e.currentTarget, min, max);
        setLocal(next);
        onChange?.(parseNumericInput(next));
    };

    const hasAffix = Boolean(prefix || suffix);

    const inputEl = (
        <input
            data-test-component='ui/forms/NumberInput'
            ref={inputRef}
            type='text'
            inputMode={rules.inputMode}
            pattern={rules.domPattern}
            className={classNames({
                'form-control': !hasAffix,
                'form-input-prefixed-input': hasAffix,
                error: !hasAffix && Boolean(error),
                [className]: !hasAffix && Boolean(className),
            })}
            {...props}
            onChange={handleChange}
            value={local}
            style={{ textAlign: align }}
        />
    );

    if (!hasAffix) return inputEl;

    return (
        <PrefixedInputShell
            prefix={prefix}
            suffix={suffix}
            value={local}
            inputRef={inputRef}
            className={classNames({
                'form-control': true,
                'form-input-prefixed': true,
                error: Boolean(error),
                [className]: Boolean(className),
            })}>
            {inputEl}
        </PrefixedInputShell>
    );
}

Input.Number = NumberInput;

function MoneyInput(props: Omit<InputProps, 'onChange'> & { onChange?: (value: number) => void }) {
    const {
        value,
        onChange,
        className = '',
        prefix,
        suffix,
        error,
        suffixAlignRight,
        ...rest
    } = props;
    const [localValue, setLocalValue] = useState<string>(sanitizeMoneyInput(String(value)));

    // updates value on external change
    useEffect(() => {
        if (Number(value) !== moneyToNumber(localValue)) {
            setLocalValue(sanitizeMoneyInput(String(value)));
        }
    }, [value]);

    // helpers to keep cursor in position
    const inputRef = useRef<HTMLInputElement>(null);

    // keeps cursor in position while editing
    const updateCursor = (cursor: number, newValue: string, oldValue: string) => {
        const countCommasLeft = (str: string, cursor: number, avoidPrevComma: boolean = false) => {
            // if cursor is after a comma and avoidPrevComma is true, we ignore that comma by moving cursor to the left
            const prevPosChar = str[Math.max(0, cursor - 1)];
            const finalPos =
                prevPosChar === ',' && avoidPrevComma ? Math.max(0, cursor - 1) : cursor;

            return (str.slice(0, finalPos).match(/,/g) || []).length;
        };

        const isAddition = oldValue.length < newValue.length;
        const isDeletion = oldValue.length > newValue.length;

        // when adding, ignore comma before cursor for old value
        const oldCommasLeft = countCommasLeft(oldValue, cursor, isAddition);
        // when deleting, ignore comma before cursor for new value
        const newCommasLeft = countCommasLeft(newValue, cursor, isDeletion);

        const commaDiff = newCommasLeft - oldCommasLeft;

        const newPos = Math.max(0, cursor + commaDiff);

        // delay to after React updates the value
        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(newPos, newPos);
        });
    };

    // move cursor at the end for 0 to act as a placeholder
    const zeroAsPlaceholder = (value: string) => {
        if (value === '0') {
            requestAnimationFrame(() => {
                inputRef.current?.setSelectionRange(1, 1);
            });
        }
    };

    return (
        <div
            className={classNames({
                [className]: Boolean(className),
                'form-control form-input-prefixed': true,
                error: Boolean(error),
            })}>
            <label
                htmlFor={props.name}
                className='form-input-prefix'>
                {prefix ?? '$'}
            </label>
            <input
                id={props.name}
                className='form-input-prefixed-input'
                style={{ width: suffix && !suffixAlignRight ? `${localValue.length}ch` : '100%' }}
                ref={inputRef}
                value={localValue}
                onKeyDown={(e) => {
                    // cancels event on any key except digits, dot, and allowed special keys
                    const allowedKeys = [
                        'ArrowLeft',
                        'ArrowRight',
                        'ArrowUp',
                        'ArrowDown',
                        'Backspace',
                        'Delete',
                        'Tab',
                        'Enter',
                        'Home',
                        'End',
                    ];

                    const isMetaKey = e.metaKey || e.ctrlKey || e.altKey;

                    if (isMetaKey) return; // allow copy, paste, etc.

                    const key = e.key;

                    const isDigit = /^[0-9]$/.test(key);
                    const isDot = key === '.';
                    const isAllowedSpecial = allowedKeys.includes(key);

                    if (!isDigit && !isDot && !isAllowedSpecial) {
                        e.preventDefault(); // block any other key
                    }
                }}
                onKeyUp={(e) => {
                    // adjust cursor position to avoid remaining after the comma
                    const pos = e.currentTarget.selectionStart || 0;
                    const isRangeSelection =
                        e.currentTarget.selectionStart !== e.currentTarget.selectionEnd;
                    const prevPosChar = e.currentTarget.value[Math.max(0, pos - 1)];

                    if (prevPosChar === ',' && !isRangeSelection) {
                        const offset = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 1;

                        // delay to after React updates the value
                        requestAnimationFrame(() => {
                            inputRef.current?.setSelectionRange(pos + offset, pos + offset);
                        });
                    }

                    zeroAsPlaceholder(e.currentTarget.value);
                }}
                onFocus={(e) => {
                    zeroAsPlaceholder(e.currentTarget.value);
                }}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const newValue = sanitizeMoneyInput(e.target.value);

                    updateCursor(e.target.selectionStart || 0, newValue, localValue);

                    setLocalValue(newValue);

                    onChange?.(moneyToNumber(newValue));
                }}
                {...rest}
            />
            {suffix && (
                <label
                    htmlFor={props.name}
                    className='form-input-suffix'>
                    {suffix}
                </label>
            )}
        </div>
    );
}

Input.Money = MoneyInput;

export type ArrayInputProps = {
    name: string;
    value: string[];
    onChange: (values: string[]) => void;
    disabled?: boolean;
    initialValue?: string[];
    inputProps?: (value: string, index: number) => Partial<InputProps>;
};
export function ArrayInput({
    name,
    value,
    onChange,
    disabled,
    initialValue = [''],
    inputProps,
}: ArrayInputProps) {
    const items = Array.isArray(value) && value.length >= 1 ? value : initialValue;

    const handleDelete = (idx: number) => {
        onChange(items.filter((_, i) => i !== idx));
    };

    const handleChange = (idx: number, value: string) => {
        onChange(items.map((v, i) => (i === idx ? value : v)));
    };

    const handleAdd = () => {
        onChange([...items, '']);
    };

    return (
        <div className='form-input-array'>
            {items.map((v, idx) => (
                <div
                    key={idx}
                    className='form-input-array-item'>
                    <Input
                        {...inputProps?.(v, idx)}
                        name={name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChange(idx, e.target.value)
                        }
                        disabled={disabled}
                        value={v}
                    />
                    {items.length - 1 !== idx && (
                        <Button
                            variant='floating'
                            action='negative'
                            icon={RemixIconNames.RiDeleteBin6Line}
                            onClick={() => handleDelete(idx)}
                            title='Delete'
                            size='medium'
                        />
                    )}

                    {items.length - 1 === idx && (
                        <Button
                            variant='filled-light'
                            icon={RemixIconNames.RiAddLine}
                            title='Add'
                            onClick={handleAdd}
                            size='medium'
                        />
                    )}
                </div>
            ))}
        </div>
    );
}

export type PhoneInputProps = {
    defaultCountry?: Country;
    value: Value;
    onChange: (value?: Value) => void;
    error?: boolean;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    id?: string;
} & FeatureProps<DefaultInputComponentProps>;

export function PhoneInput({
    defaultCountry,
    value,
    onChange,
    error,
    className = '',
    disabled = false,
    placeholder = '',
    ...props
}: PhoneInputProps) {
    return (
        <ReactPhoneInput
            {...props}
            className={classNames({ error: Boolean(error), [className]: Boolean(className) })}
            value={value}
            disabled={disabled}
            placeholder={placeholder}
            onChange={onChange}
            defaultCountry={defaultCountry}
            countryCallingCodeEditable={true}
            international
            internationalIcon={(props) => (
                <RiGlobalLine
                    size={16}
                    className={props.className}
                />
            )}
        />
    );
}
